import type { AuthBootstrapResponse } from '@overlayqa/shared';
import { useApiQuery } from './useApiQuery';
import { useUser } from '../lib/auth';
import { authApi } from '../lib/api';
import { readStashedSignupSource } from '../lib/freeScanClaim';
import { log } from '@/lib/logger';

log.info('[useAppSession] Module loaded');

export function useAppSession() {
  const { user } = useUser();
  log.info('[useAppSession] useAppSession called, userId:', { id: user?.id });

  return useApiQuery<AuthBootstrapResponse>({
    queryKey: ['app-session', user?.id],
    queryFn: async (token) => {
      log.info('[useAppSession] queryFn executing for userId:', { id: user?.id });
      const email = user?.primaryEmailAddress?.emailAddress;
      const name = user?.username || user?.firstName || 'User';
      const imageUrl = (user as { imageUrl?: string | null } | null)?.imageUrl ?? null;

      if (!email) {
        throw new Error('Not authenticated');
      }

      // Carry the free-scan attribution stashed at /sign-in (OVE-4) so the
      // server tags account_created with source='free-tool-annotate' instead of
      // 'direct'. Read at fetch time: this is the first bootstrap after sign-up,
      // exactly when the server fires the event. Null for every normal signup.
      const signupSource = readStashedSignupSource() ?? undefined;

      return authApi.bootstrap(token, {
        email,
        name,
        avatarUrl: imageUrl,
        signupSource,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });
}
